// src/modules/aiml/TransformersSection.jsx
// Transformers.js v3 — Real BERT, Whisper, CLIP, running in browser via WASM/WebGPU
// Tabs: overview | nlp | vision | audio | embeddings | zero_shot | code | concepts

import React, { useState, useRef, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, LineChart, Line,
} from 'recharts';
import { ModelLoader, WebGPUBadge, InferenceTag } from './AIMLModule.jsx';

const ACCENT = '#f59e0b';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['overview','nlp','vision','audio','embeddings','zero_shot','code','concepts'];

const Btn = ({ onClick, children, color = ACCENT, disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding: '8px 18px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700, opacity: disabled ? 0.4 : 1, ...style }}>
    {children}
  </button>
);
const Tag = ({ children, color = ACCENT }) => (
  <span style={{ padding: '2px 8px', background: color + '20', border: `1px solid ${color}44`, color, fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{children}</span>
);
const Panel = ({ title, accent = ACCENT, children, style }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: '4px', overflow: 'hidden', ...style }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b0)', fontSize: '9px', letterSpacing: '.1em', color: accent, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{title}</div>
    <div style={{ padding: '14px' }}>{children}</div>
  </div>
);

// CDN URL for Transformers.js v3
const TJS_CDN = 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.4.0/dist/transformers.min.js';

// Load Transformers.js dynamically
let transformersModule = null;
async function loadTransformers() {
  if (transformersModule) return transformersModule;
  // Use dynamic import — works in modern browsers
  transformersModule = await import(/* webpackIgnore: true */ TJS_CDN);
  return transformersModule;
}

// ── NLP Task Definitions ──────────────────────────────────────────
const NLP_TASKS = [
  {
    id: 'sentiment',
    label: 'Sentiment Analysis',
    task: 'sentiment-analysis',
    model: 'Xenova/distilbert-base-uncased-finetuned-sst-2-english',
    modelSize: '~67MB',
    desc: 'DistilBERT fine-tuned on SST-2. Classifies text as POSITIVE or NEGATIVE.',
    placeholder: 'I absolutely love this product! It exceeded all my expectations.',
    color: '#34d399',
  },
  {
    id: 'ner',
    label: 'Named Entity Recognition',
    task: 'token-classification',
    model: 'Xenova/bert-base-NER',
    modelSize: '~108MB',
    desc: 'BERT fine-tuned on CoNLL-2003. Tags entities: PER, ORG, LOC, MISC.',
    placeholder: 'Elon Musk founded SpaceX in Hawthorne, California in 2002.',
    color: '#60a5fa',
  },
  {
    id: 'qa',
    label: 'Question Answering',
    task: 'question-answering',
    model: 'Xenova/distilbert-base-uncased-distilled-squad',
    modelSize: '~67MB',
    desc: 'Extractive QA — finds the answer span within the provided context.',
    placeholder: '',
    color: '#f472b6',
  },
  {
    id: 'summarize',
    label: 'Summarization',
    task: 'summarization',
    model: 'Xenova/bart-large-cnn',
    modelSize: '~400MB',
    desc: 'BART fine-tuned on CNN/Daily Mail. Generates abstractive summaries.',
    placeholder: 'The quick brown fox jumps over the lazy dog. This is a long document that needs to be summarized into a shorter version that captures the key points.',
    color: '#a78bfa',
  },
  {
    id: 'translate',
    label: 'Translation (EN→FR)',
    task: 'translation_en_to_fr',
    model: 'Xenova/opus-mt-en-fr',
    modelSize: '~45MB',
    desc: 'Helsinki-NLP MarianMT. English to French machine translation.',
    placeholder: 'Machine learning is transforming the way we interact with computers.',
    color: '#fb923c',
  },
  {
    id: 'generate',
    label: 'Text Generation',
    task: 'text-generation',
    model: 'Xenova/gpt2',
    modelSize: '~124MB',
    desc: 'GPT-2 small. Autoregressive next-token prediction for text generation.',
    placeholder: 'The future of artificial intelligence is',
    color: ACCENT,
  },
];

// ── Vision Task Definitions ───────────────────────────────────────
const VISION_TASKS = [
  {
    id: 'classify',
    label: 'Image Classification',
    task: 'image-classification',
    model: 'Xenova/mobilenetv4_conv_small.e2400_r224_in1k',
    modelSize: '~10MB',
    desc: 'MobileNetV4 — 1000 ImageNet classes. Runs on WebGPU for instant results.',
    color: '#34d399',
  },
  {
    id: 'detect',
    label: 'Object Detection',
    task: 'object-detection',
    model: 'Xenova/detr-resnet-50',
    modelSize: '~165MB',
    desc: 'DETR (Detection Transformer) — 80 COCO object classes with bounding boxes.',
    color: '#60a5fa',
  },
  {
    id: 'depth',
    label: 'Depth Estimation',
    task: 'depth-estimation',
    model: 'Xenova/depth-anything-small-hf',
    modelSize: '~24MB',
    desc: 'Depth Anything — monocular depth estimation, outputs relative depth map.',
    color: '#f472b6',
  },
];

const QA_CONTEXT_DEFAULT = `Transformers.js is a JavaScript library for running machine learning models in the browser. 
It is designed to be functionally equivalent to Hugging Face's Python transformers library. 
Transformers.js was created by Joshua Lochner and released in 2023. 
The library supports over 1200 models and uses ONNX Runtime for inference. 
It supports WebGPU acceleration which can be up to 100 times faster than WASM.`;

const QA_QUESTION_DEFAULT = 'Who created Transformers.js?';

export default function TransformersSection({ gpuInfo }) {
  const [tab, setTab] = useState('overview');

  // NLP state
  const [nlpTask, setNlpTask]         = useState(NLP_TASKS[0]);
  const [nlpStatus, setNlpStatus]     = useState('idle');
  const [nlpProgress, setNlpProgress] = useState(0);
  const [nlpInput, setNlpInput]       = useState(NLP_TASKS[0].placeholder);
  const [nlpQACtx, setNlpQACtx]       = useState(QA_CONTEXT_DEFAULT);
  const [nlpQAQ, setNlpQAQ]           = useState(QA_QUESTION_DEFAULT);
  const [nlpResult, setNlpResult]     = useState(null);
  const [nlpTime, setNlpTime]         = useState(null);
  const [nlpHistory, setNlpHistory]   = useState([]);
  const pipelineRef = useRef({});

  // Vision state
  const [visionTask, setVisionTask]     = useState(VISION_TASKS[0]);
  const [visionStatus, setVisionStatus] = useState('idle');
  const [visionProgress, setVisionProgress] = useState(0);
  const [visionResult, setVisionResult] = useState(null);
  const [visionTime, setVisionTime]     = useState(null);
  const [visionImgSrc, setVisionImgSrc] = useState(null);
  const canvasRef = useRef(null);
  const visionPipeRef = useRef({});

  // Audio state
  const [audioStatus, setAudioStatus]   = useState('idle');
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioResult, setAudioResult]   = useState(null);
  const [audioTime, setAudioTime]       = useState(null);
  const [audioFileName, setAudioFileName] = useState('');
  const audioPipeRef = useRef(null);

  // Embeddings state
  const [embStatus, setEmbStatus]       = useState('idle');
  const [embProgress, setEmbProgress]   = useState(0);
  const [embSentences, setEmbSentences] = useState(
    'Machine learning is fascinating\nNatural language processing is a subfield of AI\nThe cat sat on the mat\nDeep learning uses neural networks\nThe dog chased the ball'
  );
  const [embResult, setEmbResult]       = useState(null);
  const embPipeRef = useRef(null);

  // Zero-shot
  const [zsStatus, setZsStatus]         = useState('idle');
  const [zsProgress, setZsProgress]     = useState(0);
  const [zsInput, setZsInput]           = useState('The new iPhone has incredible camera features and a powerful chip');
  const [zsLabels, setZsLabels]         = useState('technology, sports, politics, entertainment, science, business');
  const [zsResult, setZsResult]         = useState(null);
  const [zsTime, setZsTime]             = useState(null);
  const zsPipeRef = useRef(null);

  // ── NLP ──────────────────────────────────────────────────────────
  const loadNLPModel = async () => {
    setNlpStatus('downloading');
    setNlpProgress(0);
    try {
      const { pipeline, env } = await loadTransformers();
      env.allowRemoteModels = true;
      env.allowLocalModels  = false;

      const pipe = await pipeline(nlpTask.task, nlpTask.model, {
        dtype: 'q8',
        progress_callback: (p) => {
          if (p.status === 'downloading') setNlpProgress(Math.round((p.loaded / p.total) * 100) || 0);
          else if (p.status === 'loading') { setNlpStatus('loading'); setNlpProgress(100); }
        },
      });
      pipelineRef.current[nlpTask.id] = pipe;
      setNlpStatus('ready');
    } catch (err) {
      console.error('NLP load error:', err);
      setNlpStatus('error');
    }
  };

  const runNLP = async () => {
    const pipe = pipelineRef.current[nlpTask.id];
    if (!pipe) return;
    const t0 = performance.now();
    try {
      let result;
      if (nlpTask.id === 'qa') {
        result = await pipe({ question: nlpQAQ, context: nlpQACtx });
      } else if (nlpTask.id === 'generate') {
        result = await pipe(nlpInput, { max_new_tokens: 60, temperature: 0.9 });
      } else if (nlpTask.id === 'summarize') {
        result = await pipe(nlpInput, { max_length: 60, min_length: 20 });
      } else {
        result = await pipe(nlpInput);
      }
      const ms = Math.round(performance.now() - t0);
      setNlpResult(result);
      setNlpTime(ms);
      setNlpHistory(h => [...h.slice(-7), { task: nlpTask.label, ms }]);
    } catch (err) {
      console.error('NLP run error:', err);
    }
  };

  const switchNLPTask = (task) => {
    setNlpTask(task);
    setNlpResult(null);
    setNlpTime(null);
    setNlpInput(task.placeholder);
    // reset status if different model not yet loaded
    if (!pipelineRef.current[task.id]) {
      setNlpStatus('idle');
      setNlpProgress(0);
    } else {
      setNlpStatus('ready');
    }
  };

  // ── VISION ───────────────────────────────────────────────────────
  const loadVisionModel = async () => {
    setVisionStatus('downloading');
    setVisionProgress(0);
    try {
      const { pipeline, env } = await loadTransformers();
      env.allowRemoteModels = true;
      const pipe = await pipeline(visionTask.task, visionTask.model, {
        dtype: 'q8',
        progress_callback: (p) => {
          if (p.status === 'downloading') setVisionProgress(Math.round((p.loaded / p.total) * 100) || 0);
          else if (p.status === 'loading') { setVisionStatus('loading'); setVisionProgress(100); }
        },
      });
      visionPipeRef.current[visionTask.id] = pipe;
      setVisionStatus('ready');
    } catch (err) {
      console.error('Vision load error:', err);
      setVisionStatus('error');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setVisionImgSrc(url);
    setVisionResult(null);
  };

  const runVision = async () => {
    const pipe = visionPipeRef.current[visionTask.id];
    if (!pipe || !visionImgSrc) return;
    const t0 = performance.now();
    try {
      const result = await pipe(visionImgSrc, { topk: 5 });
      setVisionTime(Math.round(performance.now() - t0));
      setVisionResult(result);

      // Draw bounding boxes for detection
      if (visionTask.id === 'detect' && canvasRef.current && Array.isArray(result)) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          canvas.width  = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          result.forEach(({ label, score, box }, i) => {
            const { xmin, ymin, xmax, ymax } = box;
            const color = P[i % P.length];
            ctx.strokeStyle = color;
            ctx.lineWidth   = 3;
            ctx.strokeRect(xmin, ymin, xmax - xmin, ymax - ymin);
            ctx.fillStyle = color;
            ctx.fillRect(xmin, ymin - 20, (label.length + 6) * 7, 20);
            ctx.fillStyle = '#000';
            ctx.font = 'bold 12px monospace';
            ctx.fillText(`${label} ${(score * 100).toFixed(0)}%`, xmin + 4, ymin - 5);
          });
        };
        img.src = visionImgSrc;
      }
    } catch (err) {
      console.error('Vision run error:', err);
    }
  };

  // ── AUDIO ────────────────────────────────────────────────────────
  const loadAudioModel = async () => {
    setAudioStatus('downloading');
    setAudioProgress(0);
    try {
      const { pipeline, env } = await loadTransformers();
      env.allowRemoteModels = true;
      const pipe = await pipeline('automatic-speech-recognition', 'Xenova/whisper-tiny.en', {
        dtype: 'q8',
        progress_callback: (p) => {
          if (p.status === 'downloading') setAudioProgress(Math.round((p.loaded / p.total) * 100) || 0);
          else if (p.status === 'loading') { setAudioStatus('loading'); setAudioProgress(100); }
        },
      });
      audioPipeRef.current = pipe;
      setAudioStatus('ready');
    } catch (err) {
      console.error('Audio load error:', err);
      setAudioStatus('error');
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !audioPipeRef.current) return;
    setAudioFileName(file.name);
    const t0 = performance.now();
    try {
      const url = URL.createObjectURL(file);
      const result = await audioPipeRef.current(url, {
        chunk_length_s: 30,
        return_timestamps: true,
      });
      setAudioTime(Math.round(performance.now() - t0));
      setAudioResult(result);
    } catch (err) {
      console.error('Audio run error:', err);
    }
  };

  // ── EMBEDDINGS ───────────────────────────────────────────────────
  const loadEmbModel = async () => {
    setEmbStatus('downloading');
    setEmbProgress(0);
    try {
      const { pipeline, env } = await loadTransformers();
      env.allowRemoteModels = true;
      const pipe = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2', {
        dtype: 'q8',
        progress_callback: (p) => {
          if (p.status === 'downloading') setEmbProgress(Math.round((p.loaded / p.total) * 100) || 0);
          else if (p.status === 'loading') { setEmbStatus('loading'); setEmbProgress(100); }
        },
      });
      embPipeRef.current = pipe;
      setEmbStatus('ready');
    } catch (err) {
      console.error('Emb load error:', err);
      setEmbStatus('error');
    }
  };

  const cosineSim = (a, b) => {
    let dot = 0, na = 0, nb = 0;
    for (let i = 0; i < a.length; i++) { dot += a[i] * b[i]; na += a[i] ** 2; nb += b[i] ** 2; }
    return dot / (Math.sqrt(na) * Math.sqrt(nb));
  };

  const runEmbeddings = async () => {
    if (!embPipeRef.current) return;
    const sents = embSentences.split('\n').map(s => s.trim()).filter(Boolean);
    try {
      const outputs = await embPipeRef.current(sents, { pooling: 'mean', normalize: true });
      const vecs = outputs.tolist ? outputs.tolist() : Array.from(outputs.data).reduce((acc, v, i) => {
        const row = Math.floor(i / outputs.dims[1]);
        if (!acc[row]) acc[row] = [];
        acc[row].push(v);
        return acc;
      }, []);
      // Build similarity matrix
      const matrix = vecs.map(v1 => vecs.map(v2 => +cosineSim(v1, v2).toFixed(3)));
      setEmbResult({ sents, vecs, matrix, dim: vecs[0]?.length });
    } catch (err) {
      console.error('Emb run error:', err);
    }
  };

  // ── ZERO-SHOT ────────────────────────────────────────────────────
  const loadZSModel = async () => {
    setZsStatus('downloading');
    setZsProgress(0);
    try {
      const { pipeline, env } = await loadTransformers();
      env.allowRemoteModels = true;
      const pipe = await pipeline('zero-shot-classification', 'Xenova/nli-deberta-v3-small', {
        dtype: 'q8',
        progress_callback: (p) => {
          if (p.status === 'downloading') setZsProgress(Math.round((p.loaded / p.total) * 100) || 0);
          else if (p.status === 'loading') { setZsStatus('loading'); setZsProgress(100); }
        },
      });
      zsPipeRef.current = pipe;
      setZsStatus('ready');
    } catch (err) {
      console.error('ZS load error:', err);
      setZsStatus('error');
    }
  };

  const runZeroShot = async () => {
    if (!zsPipeRef.current) return;
    const labels = zsLabels.split(',').map(l => l.trim()).filter(Boolean);
    const t0 = performance.now();
    try {
      const result = await zsPipeRef.current(zsInput, labels, { multi_label: true });
      setZsTime(Math.round(performance.now() - t0));
      setZsResult(result);
    } catch (err) {
      console.error('ZS run error:', err);
    }
  };

  // ── RENDER HELPERS ───────────────────────────────────────────────
  const renderNLPResult = () => {
    if (!nlpResult) return null;
    const r = Array.isArray(nlpResult) ? nlpResult : [nlpResult];

    if (nlpTask.id === 'sentiment') {
      const top = r[0];
      const isPos = top.label === 'POSITIVE';
      return (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '14px', background: (isPos ? 'var(--green)' : 'var(--red)') + '12', borderRadius: '4px', border: `1px solid ${isPos ? 'var(--green)' : 'var(--red)'}33` }}>
          <div style={{ fontSize: '28px' }}>{isPos ? '😊' : '😔'}</div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, fontFamily: 'Syne', color: isPos ? 'var(--green)' : 'var(--red)' }}>{top.label}</div>
            <div style={{ fontSize: '11px', color: 'var(--t3)' }}>confidence: {(top.score * 100).toFixed(1)}%</div>
          </div>
          <div style={{ marginLeft: 'auto' }}><InferenceTag ms={nlpTime} /></div>
        </div>
      );
    }

    if (nlpTask.id === 'ner') {
      const entities = Array.isArray(r[0]) ? r[0] : r;
      const COLOR_MAP = { PER: '#f472b6', ORG: '#60a5fa', LOC: '#34d399', MISC: '#a78bfa' };
      return (
        <div>
          <div style={{ lineHeight: 2.2, fontSize: '13px', padding: '12px', background: 'var(--deep)', borderRadius: '4px', marginBottom: '12px' }}>
            {nlpInput.split(' ').map((word, i) => {
              const ent = entities.find(e => e.word?.replace('##', '') === word || e.word === word);
              const type = ent?.entity?.replace('B-', '').replace('I-', '') || null;
              const col = type ? COLOR_MAP[type] || ACCENT : null;
              return (
                <span key={i}>
                  {col
                    ? <span style={{ color: col, fontWeight: 700, background: col + '20', padding: '1px 4px', borderRadius: '2px' }}>{word}<sup style={{ fontSize: '7px', marginLeft: '2px' }}>{type}</sup></span>
                    : word
                  }
                  {' '}
                </span>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {entities.map((e, i) => (
              <span key={i} style={{ padding: '3px 10px', background: (COLOR_MAP[e.entity?.replace('B-', '')?.replace('I-', '')] || ACCENT) + '22', border: `1px solid ${(COLOR_MAP[e.entity?.replace('B-', '')?.replace('I-', '')] || ACCENT)}44`, color: COLOR_MAP[e.entity?.replace('B-', '')?.replace('I-', '')] || ACCENT, fontSize: '10px', borderRadius: '3px', fontFamily: 'JetBrains Mono' }}>
                {e.word} — {e.entity}
              </span>
            ))}
          </div>
        </div>
      );
    }

    if (nlpTask.id === 'qa') {
      const res = Array.isArray(r) ? r[0] : nlpResult;
      return (
        <div style={{ padding: '14px', background: 'var(--raised)', borderRadius: '4px', border: `1px solid ${ACCENT}33` }}>
          <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>ANSWER</div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: ACCENT, fontFamily: 'Syne', marginBottom: '6px' }}>{res.answer}</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tag>score: {(res.score * 100).toFixed(1)}%</Tag>
            <Tag color="var(--cyan)">start: {res.start}</Tag>
            <Tag color="var(--cyan)">end: {res.end}</Tag>
            <InferenceTag ms={nlpTime} />
          </div>
        </div>
      );
    }

    if (nlpTask.id === 'generate') {
      const text = r[0]?.generated_text || r[0]?.text || '';
      return (
        <div style={{ padding: '14px', background: 'var(--deep)', borderRadius: '4px', border: `1px solid ${ACCENT}22`, fontSize: '13px', color: 'var(--t1)', lineHeight: 1.8, fontFamily: 'JetBrains Mono' }}>
          <span style={{ color: 'var(--cyan)' }}>{nlpInput}</span>
          <span style={{ color: ACCENT }}>{text.slice(nlpInput.length)}</span>
        </div>
      );
    }

    if (nlpTask.id === 'summarize') {
      return (
        <div style={{ padding: '14px', background: 'var(--raised)', borderRadius: '4px', border: `1px solid ${ACCENT}33` }}>
          <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>SUMMARY</div>
          <div style={{ fontSize: '13px', color: 'var(--t1)', lineHeight: 1.8 }}>{r[0]?.summary_text}</div>
        </div>
      );
    }

    if (nlpTask.id === 'translate') {
      return (
        <div style={{ padding: '14px', background: 'var(--raised)', borderRadius: '4px', border: `1px solid ${ACCENT}33` }}>
          <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '6px', letterSpacing: '.1em' }}>TRANSLATION (EN→FR)</div>
          <div style={{ fontSize: '14px', color: ACCENT, lineHeight: 1.8, fontStyle: 'italic' }}>{r[0]?.translation_text}</div>
        </div>
      );
    }

    return <pre style={{ fontSize: '11px', color: 'var(--t2)', fontFamily: 'JetBrains Mono' }}>{JSON.stringify(r, null, 2)}</pre>;
  };

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '3px', background: 'var(--deep)', padding: '4px', borderRadius: '3px', border: '1px solid var(--b0)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', border: `1px solid ${tab === t ? ACCENT : 'transparent'}`, background: tab === t ? ACCENT + '18' : 'transparent', color: tab === t ? ACCENT : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', fontWeight: tab === t ? 700 : 400 }}>
            {t.toUpperCase().replace('_', '-')}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="TRANSFORMERS.JS v3 — LIBRARY OVERVIEW" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.8 }}>
                <strong style={{ color: ACCENT }}>Transformers.js v3</strong> (Oct 2024) runs HuggingFace models directly in your browser using ONNX Runtime. No server, no Python, no API keys — 100% client-side.<br /><br />
                Backed by <strong style={{ color: 'var(--cyan)' }}>ONNX Runtime Web</strong> which compiles models to WebAssembly or WebGPU shaders for GPU-accelerated inference. First load downloads model weights (cached in IndexedDB), subsequent runs are instant.<br /><br />
                <strong style={{ color: 'var(--green)' }}>WebGPU</strong> (Chrome 113+, Edge) provides up to <strong style={{ color: 'var(--gold)' }}>100× speedup</strong> over WASM for large models. WASM with SIMD is the universal fallback.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { label: 'Models available', value: '1,200+', color: ACCENT },
                  { label: 'WASM performance', value: '~10–200ms', color: 'var(--cyan)' },
                  { label: 'WebGPU speedup', value: 'up to 100×', color: 'var(--green)' },
                  { label: 'Quantization', value: 'q4 / q8 / fp16 / fp32', color: 'var(--violet)' },
                  { label: 'Cache', value: 'IndexedDB (permanent)', color: 'var(--gold)' },
                  { label: 'CDN', value: 'cdn.jsdelivr.net', color: 'var(--orange)' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--t3)' }}>{label}</span>
                    <span style={{ fontSize: '11px', color, fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          {/* Supported tasks grid */}
          <Panel title="SUPPORTED TASKS (all run in your browser)" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
              {[
                { mod: '📝 NLP', tasks: ['Sentiment analysis','Named entity recognition','Question answering','Summarization','Translation','Text generation','Fill-mask','Token classification'] },
                { mod: '🖼️ Vision', tasks: ['Image classification','Object detection','Image segmentation','Depth estimation','Image-to-text','Visual QA','Zero-shot object detect','Feature extraction'] },
                { mod: '🗣️ Audio', tasks: ['Speech recognition (ASR)','Audio classification','Text-to-speech','Speaker diarization','Language identification','Zero-shot audio classify','Audio feature extraction','Music/sound analysis'] },
                { mod: '🐙 Multimodal', tasks: ['CLIP embeddings','Zero-shot image classify','Image-text matching','Document understanding','Visual grounding','Cross-modal retrieval','Vision-language models','Embedding + similarity'] },
              ].map(({ mod, tasks }) => (
                <div key={mod} style={{ padding: '12px', background: 'var(--raised)', border: '1px solid var(--b0)', borderRadius: '4px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: ACCENT, marginBottom: '8px' }}>{mod}</div>
                  {tasks.map(t => (
                    <div key={t} style={{ fontSize: '10px', color: 'var(--t3)', padding: '3px 0', borderBottom: '1px solid var(--b0)33', lineHeight: 1.5 }}>· {t}</div>
                  ))}
                </div>
              ))}
            </div>
          </Panel>

          {/* Quantization comparison */}
          <Panel title="QUANTIZATION — SIZE vs QUALITY TRADEOFF" accent={ACCENT}>
            {(()=>{
              const data = [
                { dtype:'fp32', size:100, quality:100, speed:1 },
                { dtype:'fp16', size:50,  quality:99,  speed:1.8 },
                { dtype:'q8',   size:25,  quality:97,  speed:2.5 },
                { dtype:'q4',   size:12,  quality:93,  speed:3.5 },
              ];
              return (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    {data.map(({ dtype, size, quality, speed }) => (
                      <div key={dtype} style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, fontFamily: 'JetBrains Mono', color: ACCENT }}>{dtype}</span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ fontSize: '10px', color: 'var(--t3)' }}>size: {size}%</span>
                            <span style={{ fontSize: '10px', color: 'var(--green)' }}>quality: {quality}%</span>
                            <span style={{ fontSize: '10px', color: 'var(--cyan)' }}>{speed}× faster</span>
                          </div>
                        </div>
                        <div style={{ height: '6px', background: 'var(--deep)', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${size}%`, background: ACCENT, opacity: size / 100 + 0.3, borderRadius: '3px' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.8 }}>
                    <strong style={{ color: 'var(--gold)' }}>Recommendation:</strong><br />
                    Use <code style={{ color: ACCENT }}>q8</code> for best quality/size balance (default for WASM).<br />
                    Use <code style={{ color: ACCENT }}>fp16</code> for WebGPU (default for GPU runs).<br />
                    Use <code style={{ color: ACCENT }}>q4</code> for largest models on low-memory devices.<br /><br />
                    <strong style={{ color: 'var(--cyan)' }}>All tabs in this section use q8</strong> to balance quality and download size.
                  </div>
                </div>
              );
            })()}
          </Panel>
        </div>
      )}

      {/* ── NLP ── */}
      {tab === 'nlp' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Task selector */}
          <Panel title="SELECT NLP TASK" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '4px' }}>
              {NLP_TASKS.map(t => (
                <button key={t.id} onClick={() => switchNLPTask(t)} style={{ padding: '10px 12px', border: `1px solid ${nlpTask.id === t.id ? t.color : 'var(--b0)'}`, background: nlpTask.id === t.id ? t.color + '18' : 'var(--raised)', color: nlpTask.id === t.id ? t.color : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: nlpTask.id === t.id ? 700 : 400, textAlign: 'left' }}>
                  <div style={{ fontWeight: 700, marginBottom: '3px' }}>{t.label}</div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', fontWeight: 400 }}>{t.model.split('/')[1]}</div>
                </button>
              ))}
            </div>
          </Panel>

          {/* Model card */}
          <div style={{ padding: '12px 14px', background: 'var(--raised)', border: `1px solid ${nlpTask.color}33`, borderRadius: '4px', display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: nlpTask.color, marginBottom: '4px', fontFamily: 'JetBrains Mono' }}>{nlpTask.model}</div>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.6 }}>{nlpTask.desc}</div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
              <Tag color={nlpTask.color}>{nlpTask.modelSize}</Tag>
              {pipelineRef.current[nlpTask.id] && <Tag color="var(--green)">✓ cached</Tag>}
            </div>
          </div>

          {/* Loader */}
          {!pipelineRef.current[nlpTask.id] && (
            <ModelLoader
              status={nlpStatus}
              progress={nlpProgress}
              modelName={nlpTask.model.split('/')[1]}
              modelSize={nlpTask.modelSize}
              accent={nlpTask.color}
              onLoad={loadNLPModel}
            />
          )}

          {/* Input */}
          {(nlpStatus === 'ready' || pipelineRef.current[nlpTask.id]) && (
            <Panel title="INPUT" accent={nlpTask.color}>
              {nlpTask.id === 'qa' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px', letterSpacing: '.1em' }}>CONTEXT</div>
                    <textarea value={nlpQACtx} onChange={e => setNlpQACtx(e.target.value)} rows={5} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '10px 12px', fontSize: '12px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px', letterSpacing: '.1em' }}>QUESTION</div>
                    <input value={nlpQAQ} onChange={e => setNlpQAQ(e.target.value)} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '10px 12px', fontSize: '12px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
              ) : (
                <textarea value={nlpInput} onChange={e => setNlpInput(e.target.value)} rows={nlpTask.id === 'summarize' ? 5 : 3} placeholder={nlpTask.placeholder} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '10px 12px', fontSize: '13px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box' }} />
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                <Btn onClick={runNLP} color={nlpTask.color}>RUN {nlpTask.label.toUpperCase()} →</Btn>
                {nlpTime && <InferenceTag ms={nlpTime} />}
              </div>
            </Panel>
          )}

          {/* Result */}
          {nlpResult && (
            <Panel title="RESULT" accent={nlpTask.color}>
              {renderNLPResult()}
            </Panel>
          )}

          {/* History */}
          {nlpHistory.length > 1 && (
            <Panel title="INFERENCE LATENCY HISTORY" accent={ACCENT}>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={nlpHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" />
                  <XAxis dataKey="task" stroke="var(--t3)" tick={{ fontSize: 8 }} angle={-10} textAnchor="end" height={40} interval={0} />
                  <YAxis stroke="var(--t3)" tick={{ fontSize: 9 }} tickFormatter={v => `${v}ms`} />
                  <Tooltip formatter={v => [`${v}ms`, 'latency']} />
                  <Bar dataKey="ms" radius={[2, 2, 0, 0]}>
                    {nlpHistory.map((_, i) => <Cell key={i} fill={P[i % P.length]} opacity={0.85} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          )}
        </div>
      )}

      {/* ── VISION ── */}
      {tab === 'vision' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="SELECT VISION TASK" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '4px' }}>
              {VISION_TASKS.map(t => (
                <button key={t.id} onClick={() => { setVisionTask(t); setVisionResult(null); if (!visionPipeRef.current[t.id]) { setVisionStatus('idle'); setVisionProgress(0); } else setVisionStatus('ready'); }} style={{ padding: '10px 12px', border: `1px solid ${visionTask.id === t.id ? t.color : 'var(--b0)'}`, background: visionTask.id === t.id ? t.color + '18' : 'var(--raised)', color: visionTask.id === t.id ? t.color : 'var(--t2)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: visionTask.id === t.id ? 700 : 400, textAlign: 'left' }}>
                  <div style={{ fontWeight: 700, marginBottom: '3px' }}>{t.label}</div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', fontWeight: 400 }}>{t.modelSize}</div>
                </button>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: 'var(--t3)', marginTop: '8px' }}>{visionTask.desc}</div>
          </Panel>

          {!visionPipeRef.current[visionTask.id] && (
            <ModelLoader status={visionStatus} progress={visionProgress} modelName={visionTask.model.split('/')[1]} modelSize={visionTask.modelSize} accent={visionTask.color} onLoad={loadVisionModel} />
          )}

          {(visionStatus === 'ready' || visionPipeRef.current[visionTask.id]) && (
            <Panel title="UPLOAD IMAGE" accent={visionTask.color}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <label style={{ padding: '10px 20px', background: visionTask.color + '18', border: `1px solid ${visionTask.color}`, color: visionTask.color, fontSize: '11px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                  📁 UPLOAD IMAGE
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                </label>
                {visionImgSrc && <Btn onClick={runVision} color={visionTask.color}>RUN {visionTask.label.toUpperCase()} →</Btn>}
                {visionTime && <InferenceTag ms={visionTime} />}
              </div>

              {visionImgSrc && (
                <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                  {visionTask.id === 'detect' ? (
                    <canvas ref={canvasRef} style={{ maxWidth: '100%', borderRadius: '4px', border: '1px solid var(--b0)' }} />
                  ) : (
                    <img src={visionImgSrc} alt="input" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  )}
                </div>
              )}
            </Panel>
          )}

          {visionResult && visionTask.id === 'classify' && Array.isArray(visionResult) && (
            <Panel title="CLASSIFICATION RESULTS" accent={visionTask.color}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {visionResult.map(({ label, score }, i) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 12px', background: 'var(--raised)', borderRadius: '3px', border: i === 0 ? `1px solid ${visionTask.color}44` : '1px solid var(--b0)' }}>
                    <span style={{ fontSize: '10px', color: 'var(--t3)', fontFamily: 'JetBrains Mono', minWidth: '22px' }}>#{i + 1}</span>
                    <span style={{ fontSize: '12px', color: 'var(--t1)', flex: 1, fontWeight: i === 0 ? 700 : 400 }}>{label}</span>
                    <div style={{ width: '120px', height: '6px', background: 'var(--deep)', borderRadius: '3px' }}>
                      <div style={{ height: '100%', width: `${score * 100}%`, background: P[i], borderRadius: '3px' }} />
                    </div>
                    <span style={{ fontSize: '11px', color: P[i], fontFamily: 'JetBrains Mono', fontWeight: 700, minWidth: '44px', textAlign: 'right' }}>{(score * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {visionResult && visionTask.id === 'detect' && Array.isArray(visionResult) && (
            <Panel title="DETECTED OBJECTS" accent={visionTask.color}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px' }}>
                {visionResult.map(({ label, score }, i) => (
                  <div key={i} style={{ padding: '8px 10px', background: 'var(--raised)', borderRadius: '3px', border: `1px solid ${P[i % P.length]}33` }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: P[i % P.length] }}>{label}</div>
                    <div style={{ fontSize: '10px', color: 'var(--t3)' }}>{(score * 100).toFixed(1)}% confidence</div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ── AUDIO ── */}
      {tab === 'audio' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="WHISPER-TINY — AUTOMATIC SPEECH RECOGNITION" accent="#a78bfa">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75, marginBottom: '14px' }}>
              OpenAI's <strong style={{ color: '#a78bfa' }}>Whisper Tiny</strong> (~40MB q8) running entirely in your browser via ONNX Runtime. Supports English with timestamps. Upload any .wav, .mp3, .ogg file to transcribe.
            </div>
            {audioStatus !== 'ready' && (
              <ModelLoader status={audioStatus} progress={audioProgress} modelName="whisper-tiny.en" modelSize="~40MB (q8)" accent="#a78bfa" onLoad={loadAudioModel} />
            )}
            {audioStatus === 'ready' && (
              <div>
                <label style={{ display: 'inline-block', padding: '10px 20px', background: '#a78bfa18', border: '1px solid #a78bfa', color: '#a78bfa', fontSize: '11px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
                  🎙️ UPLOAD AUDIO FILE
                  <input type="file" accept="audio/*" onChange={handleAudioUpload} style={{ display: 'none' }} />
                </label>
                {audioFileName && <span style={{ marginLeft: '12px', fontSize: '11px', color: 'var(--t3)' }}>{audioFileName}</span>}
                {audioTime && <span style={{ marginLeft: '12px' }}><InferenceTag ms={audioTime} /></span>}
              </div>
            )}
          </Panel>

          {audioResult && (
            <Panel title="TRANSCRIPTION" accent="#a78bfa">
              <div style={{ padding: '14px', background: 'var(--deep)', borderRadius: '4px', marginBottom: '12px', fontSize: '14px', color: 'var(--t1)', lineHeight: 1.8, border: '1px solid #a78bfa22' }}>
                {audioResult.text}
              </div>
              {audioResult.chunks && audioResult.chunks.length > 0 && (
                <>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>WORD-LEVEL TIMESTAMPS</div>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    {audioResult.chunks.slice(0, 40).map((chunk, i) => (
                      <span key={i} style={{ padding: '3px 8px', background: 'var(--raised)', border: '1px solid var(--b0)', borderRadius: '2px', fontSize: '11px', color: 'var(--t2)', fontFamily: 'JetBrains Mono' }} title={`${chunk.timestamp?.[0]?.toFixed(2)}s — ${chunk.timestamp?.[1]?.toFixed(2)}s`}>
                        {chunk.text}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </Panel>
          )}

          <Panel title="AUDIO TASKS ROADMAP" accent="#a78bfa">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
              {[
                { name: 'Whisper-tiny ASR', status: 'live', model: 'whisper-tiny.en', color: 'var(--green)' },
                { name: 'Audio Classification', status: 'next', model: 'YAMNet / wav2vec', color: 'var(--gold)' },
                { name: 'Text-to-Speech', status: 'next', model: 'Kokoro-82M', color: 'var(--gold)' },
                { name: 'Language ID', status: 'planned', model: 'whisper-language', color: 'var(--t3)' },
                { name: 'Speaker Diarization', status: 'planned', model: 'pyannote', color: 'var(--t3)' },
                { name: 'Music Analysis', status: 'planned', model: 'music21 + ONNX', color: 'var(--t3)' },
              ].map(({ name, status, model, color }) => (
                <div key={name} style={{ padding: '10px 12px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '3px' }}>
                  <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color, flex: 1 }}>{name}</span>
                    <Tag color={color}>{status}</Tag>
                  </div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)' }}>{model}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── EMBEDDINGS ── */}
      {tab === 'embeddings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="SENTENCE EMBEDDINGS — all-MiniLM-L6-v2" accent="#34d399">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75, marginBottom: '14px' }}>
              <strong style={{ color: '#34d399' }}>MiniLM-L6-v2</strong> (~22MB q8) encodes sentences into 384-dimensional vectors. Cosine similarity between vectors reflects semantic relatedness. Industry standard for semantic search and clustering.
            </div>
            {embStatus !== 'ready' && (
              <ModelLoader status={embStatus} progress={embProgress} modelName="all-MiniLM-L6-v2" modelSize="~22MB (q8)" accent="#34d399" onLoad={loadEmbModel} />
            )}
          </Panel>

          {embStatus === 'ready' && (
            <Panel title="SENTENCES (one per line)" accent="#34d399">
              <textarea value={embSentences} onChange={e => setEmbSentences(e.target.value)} rows={6} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '10px 12px', fontSize: '12px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box', marginBottom: '10px' }} />
              <Btn onClick={runEmbeddings} color="#34d399">COMPUTE EMBEDDINGS →</Btn>
            </Panel>
          )}

          {embResult && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px' }}>
                {[
                  { label: 'Sentences', value: embResult.sents.length, color: '#34d399' },
                  { label: 'Embedding dim', value: embResult.dim, color: '#60a5fa' },
                  { label: 'Total params', value: `${embResult.sents.length}×${embResult.dim}`, color: '#a78bfa' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ padding: '12px', background: 'var(--raised)', border: `1px solid ${color}22`, borderRadius: '3px', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px' }}>{label.toUpperCase()}</div>
                    <div style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'Syne', color }}>{value}</div>
                  </div>
                ))}
              </div>

              <Panel title="COSINE SIMILARITY MATRIX" accent="#34d399">
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '10px', fontFamily: 'JetBrains Mono' }}>
                    <thead><tr>
                      <th style={{ padding: '6px 8px', fontSize: '8px', color: 'var(--t3)' }}>—</th>
                      {embResult.sents.map((s, i) => (
                        <th key={i} style={{ padding: '6px 8px', fontSize: '8px', color: '#34d399', maxWidth: '80px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} title={s}>S{i + 1}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {embResult.matrix.map((row, i) => (
                        <tr key={i}>
                          <td style={{ padding: '6px 8px', color: '#34d399', fontWeight: 700, fontSize: '8px', maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={embResult.sents[i]}>{embResult.sents[i].slice(0, 16)}…</td>
                          {row.map((val, j) => (
                            <td key={j} style={{ padding: '6px 8px', textAlign: 'center', background: i === j ? 'var(--raised)' : `rgba(52,211,153,${val * 0.7})`, color: i === j ? 'var(--t3)' : val > 0.4 ? 'var(--void)' : 'var(--t2)', fontWeight: val > 0.5 ? 700 : 400, borderRadius: '2px', fontSize: '10px' }} title={`${embResult.sents[i]} ↔ ${embResult.sents[j]}: ${val}`}>
                              {val.toFixed(2)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── ZERO-SHOT ── */}
      {tab === 'zero_shot' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="ZERO-SHOT CLASSIFICATION — DeBERTa NLI" accent="#f472b6">
            <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75, marginBottom: '14px' }}>
              <strong style={{ color: '#f472b6' }}>Zero-shot classification</strong> classifies text into any categories you define — without any task-specific training. Uses Natural Language Inference (NLI): "Does this text entail being about CATEGORY?" Powered by DeBERTa-v3.
            </div>
            {zsStatus !== 'ready' && (
              <ModelLoader status={zsStatus} progress={zsProgress} modelName="nli-deberta-v3-small" modelSize="~90MB (q8)" accent="#f472b6" onLoad={loadZSModel} />
            )}
          </Panel>

          {zsStatus === 'ready' && (
            <Panel title="CLASSIFY" accent="#f472b6">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px', letterSpacing: '.1em' }}>INPUT TEXT</div>
                  <textarea value={zsInput} onChange={e => setZsInput(e.target.value)} rows={3} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '10px 12px', fontSize: '13px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px', letterSpacing: '.1em' }}>CANDIDATE LABELS (comma-separated — these can be anything)</div>
                  <input value={zsLabels} onChange={e => setZsLabels(e.target.value)} style={{ width: '100%', background: 'var(--deep)', border: '1px solid var(--b1)', color: 'var(--t1)', padding: '9px 12px', fontSize: '12px', fontFamily: 'JetBrains Mono', borderRadius: '3px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Btn onClick={runZeroShot} color="#f472b6">CLASSIFY →</Btn>
                  {zsTime && <InferenceTag ms={zsTime} />}
                  {[
                    ['News', 'technology, sports, politics, entertainment, science'],
                    ['Emotion', 'joy, sadness, anger, fear, surprise, disgust'],
                    ['Intent', 'purchase, complaint, inquiry, feedback, cancel'],
                  ].map(([l, labs]) => (
                    <button key={l} onClick={() => setZsLabels(labs)} style={{ padding: '6px 12px', border: '1px solid var(--b0)', background: 'var(--raised)', color: 'var(--t3)', fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#f472b6'} onMouseLeave={e => e.currentTarget.style.color = 'var(--t3)'}>{l}</button>
                  ))}
                </div>
              </div>
            </Panel>
          )}

          {zsResult && (
            <Panel title="CLASSIFICATION SCORES" accent="#f472b6">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {zsResult.labels.map((label, i) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 12px', background: 'var(--raised)', borderRadius: '3px', border: i === 0 ? '1px solid #f472b644' : '1px solid var(--b0)' }}>
                    <span style={{ fontSize: '12px', color: i === 0 ? '#f472b6' : 'var(--t1)', fontWeight: i === 0 ? 700 : 400, flex: 1, fontFamily: 'JetBrains Mono' }}>{label}</span>
                    <div style={{ width: '180px', height: '6px', background: 'var(--deep)', borderRadius: '3px' }}>
                      <div style={{ height: '100%', width: `${zsResult.scores[i] * 100}%`, background: P[i % P.length], borderRadius: '3px' }} />
                    </div>
                    <span style={{ fontSize: '11px', color: P[i % P.length], fontFamily: 'JetBrains Mono', fontWeight: 700, minWidth: '44px', textAlign: 'right' }}>{(zsResult.scores[i] * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '12px', padding: '10px 14px', background: 'var(--deep)', borderRadius: '3px', fontSize: '11px', color: 'var(--t3)', lineHeight: 1.6 }}>
                <strong style={{ color: '#f472b6' }}>How it works:</strong> For each label, the model scores: "This text is about <em>{'{'}label{'}'}</em>." using NLI entailment probability. No fine-tuning — just define labels and run.
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* ── CODE ── */}
      {tab === 'code' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="COPY-READY CODE SNIPPETS" accent={ACCENT}>
            <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '14px' }}>All snippets work directly in a browser <code style={{ color: ACCENT }}>&lt;script type="module"&gt;</code> — no bundler required.</div>
            {[
              {
                title: 'Sentiment Analysis',
                color: '#34d399',
                code: `import { pipeline } from 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.4.0/dist/transformers.min.js';

const pipe = await pipeline('sentiment-analysis',
  'Xenova/distilbert-base-uncased-finetuned-sst-2-english',
  { dtype: 'q8' }
);

const result = await pipe('I love transformers.js!');
console.log(result);
// [{ label: 'POSITIVE', score: 0.999 }]`,
              },
              {
                title: 'Whisper Speech Recognition',
                color: '#a78bfa',
                code: `import { pipeline } from 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.4.0/dist/transformers.min.js';

const transcriber = await pipeline(
  'automatic-speech-recognition',
  'Xenova/whisper-tiny.en',
  { dtype: 'q8' }
);

// Pass audio URL or File
const result = await transcriber('./audio.wav', {
  chunk_length_s: 30,
  return_timestamps: true,
});
console.log(result.text);`,
              },
              {
                title: 'Sentence Embeddings (MiniLM)',
                color: '#34d399',
                code: `import { pipeline } from 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.4.0/dist/transformers.min.js';

const extractor = await pipeline(
  'feature-extraction',
  'Xenova/all-MiniLM-L6-v2',
  { dtype: 'q8' }
);

const sentences = ['Hello world', 'Hi there'];
const output = await extractor(sentences, {
  pooling: 'mean',
  normalize: true,
});
console.log(output.tolist()); // [[...384d vectors...]]`,
              },
              {
                title: 'WebGPU Image Classification',
                color: '#60a5fa',
                code: `import { pipeline, env } from 'https://cdn.jsdelivr.net/npm/@huggingface/transformers@3.4.0/dist/transformers.min.js';

// Use WebGPU for 100× faster inference (Chrome/Edge)
const classifier = await pipeline(
  'image-classification',
  'onnx-community/mobilenetv4_conv_small.e2400_r224_in1k',
  { device: 'webgpu' }  // falls back to wasm automatically
);

const result = await classifier('./image.jpg', { topk: 5 });
console.log(result);
// [{ label: 'tiger', score: 0.92 }, ...]`,
              },
            ].map(({ title, color, code }) => (
              <div key={title} style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color }}>{title}</span>
                  <button onClick={() => navigator.clipboard?.writeText(code)} style={{ padding: '3px 10px', border: `1px solid ${color}44`, background: 'transparent', color, fontSize: '9px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono' }}>COPY</button>
                </div>
                <pre style={{ margin: 0, padding: '14px', background: 'var(--deep)', border: '1px solid var(--b0)', borderRadius: '4px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.7, overflowX: 'auto', whiteSpace: 'pre' }}>{code}</pre>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab === 'concepts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              { title: 'ONNX — The Universal Format', color: ACCENT, desc: 'Open Neural Network Exchange: a standardized model format that any framework can export (PyTorch, TensorFlow, JAX) and any runtime can run. Transformers.js converts HuggingFace models to ONNX offline using `optimum`. The .onnx file contains the full computation graph as a protobuf. ONNX Runtime Web compiles this to WASM bytecode or WebGPU shaders at load time.' },
              { title: 'WebGPU vs WASM', color: 'var(--cyan)', desc: 'WASM (WebAssembly): runs on CPU in a sandboxed bytecode environment. SIMD instructions give 4× speedup over scalar WASM. Still limited by single-thread and memory bandwidth. WebGPU: direct GPU access via modern APIs (Vulkan/Metal/DX12). Matrix multiplications parallelized across thousands of GPU cores. 10–100× faster for large models. Requires Chrome 113+ or Edge.' },
              { title: 'Model Caching (IndexedDB)', color: 'var(--violet)', desc: 'Transformers.js automatically caches downloaded model weights in IndexedDB (permanent browser storage, survives page reloads). First load: download ~20–400MB depending on model. Subsequent loads: instantaneous from cache. Cache is per-origin — different sites cache separately. Users can clear via DevTools → Application → IndexedDB.' },
              { title: 'Quantization Math', color: 'var(--gold)', desc: 'fp32: full precision, 4 bytes/param. fp16: half precision, 2 bytes, minimal quality loss. int8/q8: 1 byte/param, quantize weights to 256 discrete values (scale + zero point). int4/q4: 0.5 bytes/param, 16 values. For a 67M param model: fp32=268MB, q8=67MB, q4=34MB. Quality tradeoff: q8 loses <1% on benchmarks, q4 loses 3–5%.' },
              { title: 'Pipeline API vs AutoModel', color: 'var(--orange)', desc: '`pipeline()` is the high-level API — handles tokenization, model loading, and post-processing automatically. `AutoModel.from_pretrained()` gives raw model access for custom inference loops. `AutoTokenizer` gives tokenizer access. Pipeline is sufficient for 95% of use cases; use AutoModel when you need custom decoding, custom pooling, or access to intermediate layer outputs.' },
              { title: 'From Python to Browser', color: '#f472b6', desc: 'To run any HuggingFace model in-browser: (1) `pip install optimum[onnxruntime]`, (2) `optimum-cli export onnx --model your/model --task text-classification ./out --quantize`, (3) upload output to HuggingFace Hub with `transformers.js` tag, (4) use in browser with `pipeline("text-classification", "your/model")`. Community has already converted 1200+ models — check the `transformers.js` tag on the Hub.' },
            ].map(({ title, color, desc }) => (
              <div key={title} style={{ padding: '16px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
                <div style={{ color, fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>{title}</div>
                <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.7 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
